// Accessibility utilities for WCAG compliance

// Focus management utilities
export const trapFocus = (element) => {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  const handleTabKey = (e) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
  };

  element.addEventListener('keydown', handleTabKey);
  
  // Focus first element
  if (firstFocusable) {
    firstFocusable.focus();
  }

  return () => {
    element.removeEventListener('keydown', handleTabKey);
  };
};

// Escape key handler for modals
export const handleEscapeKey = (callback) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      callback();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

// Screen reader announcements
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Skip link functionality
export const createSkipLink = (targetId, text = 'Skip to main content') => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.textContent = text;
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-yellow-400 text-black px-4 py-2 rounded z-50';
  
  skipLink.addEventListener('click', (e) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView();
    }
  });
  
  return skipLink;
};

// Color contrast checker (basic implementation)
export const checkColorContrast = (foreground, background) => {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Calculate relative luminance
  const getLuminance = (rgb) => {
    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  
  if (!fg || !bg) return null;
  
  const fgLum = getLuminance(fg);
  const bgLum = getLuminance(bg);
  
  const contrast = (Math.max(fgLum, bgLum) + 0.05) / (Math.min(fgLum, bgLum) + 0.05);
  
  return {
    ratio: contrast,
    aa: contrast >= 4.5,
    aaa: contrast >= 7,
    aaLarge: contrast >= 3,
    aaaLarge: contrast >= 4.5
  };
};

// Keyboard navigation helpers
export const addKeyboardNavigation = (element, options = {}) => {
  const {
    onEnter = () => {},
    onSpace = () => {},
    onArrowUp = () => {},
    onArrowDown = () => {},
    onArrowLeft = () => {},
    onArrowRight = () => {},
    onEscape = () => {}
  } = options;

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
        onEnter(e);
        break;
      case ' ':
        onSpace(e);
        break;
      case 'ArrowUp':
        onArrowUp(e);
        break;
      case 'ArrowDown':
        onArrowDown(e);
        break;
      case 'ArrowLeft':
        onArrowLeft(e);
        break;
      case 'ArrowRight':
        onArrowRight(e);
        break;
      case 'Escape':
        onEscape(e);
        break;
      default:
        // No action needed for other keys
        break;
    }
  };

  element.addEventListener('keydown', handleKeyDown);
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
};

// ARIA label generators
export const generateAriaLabel = (context, data) => {
  switch (context) {
    case 'booking':
      return `Booking from ${data.from} to ${data.to} on ${data.date}. Status: ${data.status}`;
    case 'rating':
      return `Rating: ${data.rating} out of 5 stars`;
    case 'navigation':
      return `Navigate to ${data.page}`;
    case 'button':
      return `${data.action} button`;
    default:
      return '';
  }
};

// Form validation with accessibility
export const validateFormAccessibly = (form, rules) => {
  const errors = [];
  
  Object.keys(rules).forEach(fieldName => {
    const field = form.querySelector(`[name="${fieldName}"]`);
    const rule = rules[fieldName];
    
    if (!field) return;
    
    // Clear previous errors
    field.setAttribute('aria-invalid', 'false');
    const existingError = document.getElementById(`${fieldName}-error`);
    if (existingError) {
      existingError.remove();
    }
    
    // Validate field
    let isValid = true;
    let errorMessage = '';
    
    if (rule.required && !field.value.trim()) {
      isValid = false;
      errorMessage = rule.requiredMessage || `${fieldName} is required`;
    } else if (rule.pattern && !rule.pattern.test(field.value)) {
      isValid = false;
      errorMessage = rule.patternMessage || `${fieldName} format is invalid`;
    } else if (rule.minLength && field.value.length < rule.minLength) {
      isValid = false;
      errorMessage = rule.minLengthMessage || `${fieldName} must be at least ${rule.minLength} characters`;
    }
    
    if (!isValid) {
      // Mark field as invalid
      field.setAttribute('aria-invalid', 'true');
      
      // Create error message
      const errorElement = document.createElement('div');
      errorElement.id = `${fieldName}-error`;
      errorElement.className = 'text-red-500 text-sm mt-1';
      errorElement.textContent = errorMessage;
      errorElement.setAttribute('role', 'alert');
      
      // Associate error with field
      field.setAttribute('aria-describedby', errorElement.id);
      
      // Insert error after field
      field.parentNode.insertBefore(errorElement, field.nextSibling);
      
      errors.push({ field: fieldName, message: errorMessage });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// High contrast mode detection
export const detectHighContrastMode = () => {
  // Create test elements to detect high contrast mode
  const testElement = document.createElement('div');
  testElement.style.cssText = `
    position: absolute;
    top: -999px;
    width: 1px;
    height: 1px;
    background-color: rgb(31, 41, 59);
    border: 1px solid rgb(31, 41, 59);
  `;
  
  document.body.appendChild(testElement);
  
  const computedStyle = window.getComputedStyle(testElement);
  const backgroundColor = computedStyle.backgroundColor;
  const borderColor = computedStyle.borderTopColor;
  
  document.body.removeChild(testElement);
  
  // In high contrast mode, colors are often forced to specific values
  const isHighContrast = backgroundColor !== borderColor ||
                        backgroundColor === 'rgb(0, 0, 0)' ||
                        backgroundColor === 'rgb(255, 255, 255)';
  
  return isHighContrast;
};

// Reduced motion detection
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Focus visible polyfill
export const setupFocusVisible = () => {
  let hadKeyboardEvent = true;
  
  // Note: Keyboard throttle variables removed as they're not currently used
  // Can be re-added if keyboard event throttling is needed
  
  const pointerInitialPress = (e) => {
    if (e.metaKey || e.altKey || e.ctrlKey) {
      return;
    }
    hadKeyboardEvent = false;
  };
  
  const onKeyDown = (e) => {
    if (e.metaKey || e.altKey || e.ctrlKey) {
      return;
    }
    hadKeyboardEvent = true;
  };
  
  const onPointerDown = () => {
    hadKeyboardEvent = false;
  };
  
  const onFocus = (e) => {
    if (hadKeyboardEvent || e.target.matches(':focus-visible')) {
      e.target.classList.add('focus-visible');
    }
  };
  
  const onBlur = (e) => {
    e.target.classList.remove('focus-visible');
  };
  
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('pointerdown', pointerInitialPress, true);
  document.addEventListener('touchstart', onPointerDown, true);
  document.addEventListener('focus', onFocus, true);
  document.addEventListener('blur', onBlur, true);
};
